/**
 * Fraud Dashboard
 *
 * Enterprise features:
 * - Real-time fraud alerts via WebSocket
 * - Live fraud feed with infinite scroll
 * - Stats polling with stale-while-revalidate
 * - Blocklist management
 * - Risk score visualization
 */

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import StatCard from '../../components/common/StatCard';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import { fraudApi, adminApi } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useQuery, useInfiniteQuery, useMutation, useIntersectionObserver } from '../../hooks/useDataFetch';
import { useFraudAlerts } from '../../hooks/useRealtime';
import './FraudDashboard.css';

// Constants
const STATS_REFRESH_INTERVAL = 15000; // 15 seconds - fraud needs fast updates
const FEED_PAGE_SIZE = 20;

const FraudDashboard = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState('ip');
  const [realtimeAlerts, setRealtimeAlerts] = useState([]);
  const [showSpike, setShowSpike] = useState(false);
  const [spikeData, setSpikeData] = useState(null);

  // Fetch fraud stats with polling
  const {
    data: statsData,
    isLoading: loadingStats,
    error: statsError,
    refetch: refetchStats,
  } = useQuery(
    () => adminApi.dashboard(),
    {
      key: 'fraud:stats',
      cacheTTL: 10000,
      refetchInterval: STATS_REFRESH_INTERVAL,
      onError: () => {
        // Silent fail - show stale data
      },
    }
  );

  // Fetch fraud events with cursor pagination
  const {
    data: fraudEvents,
    isLoading: loadingEvents,
    isFetchingMore,
    hasMore,
    fetchMore,
    refetch: refetchEvents,
  } = useInfiniteQuery(
    (cursor) => fraudApi.events({
      cursor,
      limit: FEED_PAGE_SIZE,
      sort: 'createdAt:desc',
    }),
    {
      key: 'fraud:events',
      getNextCursor: (page) => page?.nextCursor || page?.meta?.nextCursor,
      cacheTTL: 5000, // Short TTL for fraud data
    }
  );

  // Fetch blocklist counts
  const {
    data: blocklistData,
    isLoading: loadingBlocklists,
  } = useQuery(
    () => fraudApi.rules.list(),
    {
      key: 'fraud:blocklists',
      cacheTTL: 30000,
    }
  );

  // Real-time fraud alerts
  useFraudAlerts((alert) => {
    if (alert) {
      // Add to realtime alerts (prepend)
      setRealtimeAlerts(prev => [alert, ...prev.slice(0, 49)]);

      // Check for spike detection
      if (alert.type === 'spike' || alert.spikeDetected) {
        setShowSpike(true);
        setSpikeData(alert);
      }

      // Show toast for high-risk events
      if (alert.riskScore >= 80) {
        showToast(`High risk event: ${alert.phone || alert.ip || 'Unknown'}`, 'error');
      }
    }
  });

  // Block entity mutation
  const blockMutation = useMutation(
    (entityId) => fraudApi.block(entityId),
    {
      onSuccess: () => {
        showToast('Entity blocked successfully', 'success');
        refetchEvents();
      },
      onError: (error) => {
        showToast(error.message || 'Failed to block entity', 'error');
      },
    }
  );

  // Infinite scroll observer
  const { ref: loadMoreRef } = useIntersectionObserver({
    onIntersect: () => {
      if (hasMore && !isFetchingMore) {
        fetchMore();
      }
    },
    enabled: hasMore && !isFetchingMore,
  });

  // Combine real-time alerts with fetched events
  const allEvents = useMemo(() => {
    const fetched = fraudEvents || [];
    // Merge and dedupe by ID
    const seen = new Set();
    const merged = [];

    // Real-time alerts first
    for (const alert of realtimeAlerts) {
      if (alert.id && !seen.has(alert.id)) {
        seen.add(alert.id);
        merged.push({ ...alert, isRealtime: true });
      }
    }

    // Then fetched events
    for (const event of fetched) {
      if (event.id && !seen.has(event.id)) {
        seen.add(event.id);
        merged.push(event);
      }
    }

    return merged;
  }, [fraudEvents, realtimeAlerts]);

  // Compute stats
  const stats = useMemo(() => {
    const data = statsData || {};
    return [
      {
        label: 'Blocked (24h)',
        value: formatNumber(data.fraudBlocked || data.blocked24h || 0),
        change: data.blockedGrowth ? `${data.blockedGrowth > 0 ? '+' : ''}${data.blockedGrowth}%` : undefined,
        changeType: data.blockedGrowth > 0 ? 'negative' : 'positive',
        icon: 'shield-off',
      },
      {
        label: 'Flagged',
        value: formatNumber(data.flagged || data.pendingReview || 0),
        change: 'For review',
        changeType: 'warning',
        icon: 'flag',
      },
      {
        label: 'Avg Risk Score',
        value: data.avgRiskScore || data.averageRisk || 42,
        change: 'Out of 100',
        changeType: (data.avgRiskScore || 42) > 50 ? 'negative' : 'positive',
        icon: 'activity',
      },
    ];
  }, [statsData]);

  // Blocklist counts
  const blocklistCounts = useMemo(() => {
    const data = blocklistData || {};
    return {
      ip: data.blockedIPs || data.ipCount || 0,
      phone: data.blockedPhones || data.phoneCount || 0,
      device: data.blockedDevices || data.deviceCount || 0,
    };
  }, [blocklistData]);

  // Handlers
  const handleRefresh = useCallback(() => {
    refetchStats();
    refetchEvents();
    showToast('Fraud data refreshed', 'success');
  }, [refetchStats, refetchEvents, showToast]);

  const handleDismissSpike = useCallback(() => {
    setShowSpike(false);
    setSpikeData(null);
  }, []);

  const handleBlockEntity = useCallback((event) => {
    if (event.userId) {
      blockMutation.mutate(event.userId);
    }
  }, [blockMutation]);

  // Helpers
  function formatNumber(num) {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(num >= 10000 ? 0 : 1)}K`;
    return num.toLocaleString();
  }

  function getRiskClass(score) {
    if (score >= 70) return 'risk-high';
    if (score >= 40) return 'risk-medium';
    return 'risk-low';
  }

  function getSignalIcon(type) {
    const icons = {
      geo: 'map-pin',
      voip: 'phone-off',
      velocity: 'zap',
      bot: 'cpu',
      proxy: 'shield',
      vpn: 'eye-off',
      tor: 'globe',
      emulator: 'smartphone',
      default: 'alert-circle',
    };
    return icons[type?.toLowerCase()] || icons.default;
  }

  function formatTimeAgo(timestamp) {
    if (!timestamp) return '';
    const seconds = Math.floor((Date.now() - new Date(timestamp)) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  }

  const isLoading = loadingStats && !statsData;

  return (
    <div className="fraud-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Fraud Detection Center</h1>
          <p className="page-subtitle">Monitor and manage fraud prevention</p>
        </div>
        <div className="page-actions">
          <Button
            variant="secondary"
            icon="refresh"
            onClick={handleRefresh}
            disabled={loadingStats}
          >
            {loadingStats ? 'Syncing...' : 'Refresh'}
          </Button>
          <Button
            variant="secondary"
            icon="settings"
            onClick={() => navigate('/fraud-config')}
          >
            Configure
          </Button>
          <Button variant="primary" icon="shield">
            Review Queue ({stats[1]?.value || 0})
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="stats-row">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} loading={isLoading} />
        ))}
      </div>

      {/* Spike Alert Banner */}
      {showSpike && spikeData && (
        <div className="alert alert-danger fraud-spike-alert">
          <Icon name="alert-triangle" size="md" />
          <span>
            <strong>Fraud Spike Detected:</strong>{' '}
            {spikeData.count || 47} blocks in last {spikeData.minutes || 15} minutes
            {spikeData.country && ` from ${spikeData.country}`}
          </span>
          <Button variant="ghost" size="sm" onClick={handleDismissSpike}>
            Dismiss
          </Button>
        </div>
      )}

      {/* Error Banner */}
      {statsError && (
        <div className="alert alert-warning">
          <Icon name="alert-circle" size="sm" />
          <span>Failed to load some data. Showing cached information.</span>
          <Button variant="ghost" size="sm" onClick={handleRefresh}>Retry</Button>
        </div>
      )}

      {/* Live Feed Section */}
      <section className="section">
        <div className="section-header">
          <h3 className="section-title">
            Live Fraud Feed
            {realtimeAlerts.length > 0 && (
              <span className="realtime-indicator">
                <span className="realtime-dot"></span>
                Live
              </span>
            )}
          </h3>
          <Button variant="ghost" size="sm" icon="refresh" onClick={refetchEvents}>
            Refresh
          </Button>
        </div>

        <div className="fraud-feed">
          {loadingEvents && allEvents.length === 0 ? (
            // Skeleton loading
            <div className="fraud-loading">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="fraud-event skeleton">
                  <div className="skeleton-content">
                    <div className="skeleton-line"></div>
                    <div className="skeleton-line short"></div>
                    <div className="skeleton-badges"></div>
                  </div>
                  <div className="skeleton-score"></div>
                </div>
              ))}
            </div>
          ) : allEvents.length > 0 ? (
            <>
              {allEvents.map((event, index) => (
                <div
                  key={event.id || index}
                  className={`fraud-event ${event.isRealtime ? 'realtime' : ''}`}
                >
                  <div className="fraud-event-info">
                    <div className="fraud-event-title">
                      {event.phone || event.ip || 'Unknown'} • {event.tenant || event.company || 'System'}
                      {event.isRealtime && (
                        <span className="new-badge">NEW</span>
                      )}
                    </div>
                    <div className="fraud-event-meta">
                      {formatTimeAgo(event.createdAt || event.timestamp)} •{' '}
                      {event.ip || 'N/A'} •{' '}
                      {event.location || event.country || 'Unknown'}
                    </div>
                    <div className="fraud-signals">
                      {(event.signals || []).map((signal, idx) => (
                        <span
                          key={idx}
                          className={`signal-badge signal-${signal.type || 'default'}`}
                        >
                          <Icon name={getSignalIcon(signal.type)} size="xs" />
                          {signal.label || signal.type}
                        </span>
                      ))}
                      {(!event.signals || event.signals.length === 0) && event.reason && (
                        <span className="signal-badge signal-default">
                          {event.reason}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="fraud-event-actions">
                    <div className="risk-score">
                      <div className={`risk-value ${getRiskClass(event.riskScore || event.risk || 0)}`}>
                        {event.riskScore || event.risk || 0}
                      </div>
                      <div className="risk-label">Risk</div>
                    </div>
                    {event.userId && (
                      <Button
                        variant="ghost"
                        size="xs"
                        icon="slash"
                        iconOnly
                        onClick={() => handleBlockEntity(event)}
                        disabled={blockMutation.isLoading}
                        aria-label="Block"
                      />
                    )}
                  </div>
                </div>
              ))}

              {/* Infinite scroll trigger */}
              {hasMore && (
                <div ref={loadMoreRef} className="fraud-load-more">
                  {isFetchingMore ? (
                    <div className="loading-spinner">Loading more...</div>
                  ) : (
                    <span>Scroll for more</span>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="fraud-empty">
              <Icon name="shield-check" size="xl" />
              <p>No fraud events detected</p>
              <span>Your systems are secure</span>
            </div>
          )}
        </div>
      </section>

      {/* Blocklists Section */}
      <section className="section">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'ip' ? 'active' : ''}`}
            onClick={() => setActiveTab('ip')}
          >
            IP Blocklist
          </button>
          <button
            className={`tab ${activeTab === 'phone' ? 'active' : ''}`}
            onClick={() => setActiveTab('phone')}
          >
            Phone Blocklist
          </button>
          <button
            className={`tab ${activeTab === 'device' ? 'active' : ''}`}
            onClick={() => setActiveTab('device')}
          >
            Device Blocklist
          </button>
        </div>

        <div className="alert alert-info">
          <span>
            <strong>
              {activeTab === 'ip' && 'IP Blocklist:'}
              {activeTab === 'phone' && 'Phone Blocklist:'}
              {activeTab === 'device' && 'Device Blocklist:'}
            </strong>
            {' '}
            {loadingBlocklists ? (
              <span className="loading-text">Loading...</span>
            ) : (
              <>
                {activeTab === 'ip' && `${formatNumber(blocklistCounts.ip)} IPs currently blocked`}
                {activeTab === 'phone' && `${formatNumber(blocklistCounts.phone)} phone numbers blocked`}
                {activeTab === 'device' && `${formatNumber(blocklistCounts.device)} device fingerprints blocked`}
              </>
            )}
          </span>
          <Button variant="secondary" size="sm" icon="plus">
            Add {activeTab === 'ip' ? 'IP' : activeTab === 'phone' ? 'Phone' : 'Device'}
          </Button>
        </div>
      </section>
    </div>
  );
};

export default FraudDashboard;
